import {
  ActionReducerMapBuilder,
  createAction,
  createAsyncThunk,
  createSlice,
} from "@reduxjs/toolkit";
import { AxiosError } from "axios";
import axios from "../../axios";
import {
  PaginationType,
  ResetApiState,
  ResetPaginationState,
  ResponseWrapper,
} from "../../redux.type";
import { queryStringGenerator } from "../../redux.util";
import { Account, AccountStateTypes } from "./Account.util";

const name = "account";
const extraActions = createExtraActions();

const AccountInitialState: AccountStateTypes = {
  createAccount: ResetApiState(null),
  resetCreateAccount: ResetApiState(null),
  editAccount: ResetApiState(null),
  resetEditAccount: ResetApiState(null),
  deleteAccount: ResetApiState(null),
  resetDeleteAccount: ResetApiState(null),
  accounts: ResetApiState([]),
  pagedAccounts: ResetApiState(ResetPaginationState()),
  account: ResetApiState(null),
  resetAccount: ResetApiState(null),
  resetStates: ResetApiState(null),
};

/**
 * ACTIONS
 */
function createExtraActions() {
  const createAccount = createAsyncThunk<ResponseWrapper<Account>, any>(
    `/create-${name}`,
    async (data: any, thunkApi) =>
      axios
        .post(`/${name}`, data)
        .then((response) => {
          if (response.data.success) {
            return response.data;
          } else {
            return thunkApi.rejectWithValue(response?.data);
          }
        })
        .catch((error) => thunkApi.rejectWithValue(error?.response?.data))
  );

  const resetCreateAccount = createAction(`/reset-create-${name}`);

  const editAccount = createAsyncThunk<ResponseWrapper<Account>, any>(
    `/edit-${name}`,
    async ({ data, id }: any, thunkApi) =>
      axios
        .put(`/${name}/${id}`, data)
        .then((response) => {
          if (response.data.success) {
            return response.data;
          } else {
            return thunkApi.rejectWithValue(response?.data);
          }
        })
        .catch((error) => thunkApi.rejectWithValue(error?.response?.data))
  );

  const resetEditAccount = createAction(`/reset-edit-${name}`);

  const deleteAccount = createAsyncThunk<number, any>(
    `/delete-${name}`,
    async (id: any, thunkApi) =>
      axios
        .delete(`${name}/${id}`)
        .then((response) => {
          if (response.data.success) {
            return id;
          } else {
            return thunkApi.rejectWithValue(response?.data);
          }
        })
        .catch((error: AxiosError) => {
          return thunkApi.rejectWithValue(error?.response?.data);
        })
  );

  const resetDeleteAccount = createAction(`/reset-delete-${name}`);

  const getAccounts = createAsyncThunk<Account[], any>(
    `/get-${name}s`,
    async (query: any, thunkApi) => {
      return axios
        .get(`/${name}${queryStringGenerator(query)}`)
        .then((response) => {
          if (response.data) {
            return response.data;
          } else {
            return thunkApi.rejectWithValue(response?.data);
          }
        })
        .catch((error: AxiosError) => {
          return thunkApi.rejectWithValue(error.message);
        });
    }
  );

  const getPagedAccounts = createAsyncThunk<PaginationType<Account[]>, any>(
    `/get-paged-${name}s`,
    async (query: any, thunkApi) => {
      return axios
        .get(`/${name}/paged${queryStringGenerator(query)}`)
        .then((response) => {
          if (response.data) {
            return response.data;
          } else {
            return thunkApi.rejectWithValue(response?.data);
          }
        })
        .catch((error: AxiosError) => {
          return thunkApi.rejectWithValue(error.message);
        });
    }
  );

  const getAccount = createAsyncThunk<Account, any>(
    `/get-${name}`,
    async (id: any, thunkApi) =>
      axios
        .get(`${name}/${id}`)
        .then((response) => response.data)
        .catch((error) => thunkApi.rejectWithValue(error?.response?.data))
  );

  const resetAccount = createAction(`/reset-get-${name}`);

  const resetStates = createAction(`/reset-states`);

  return {
    createAccount,
    resetCreateAccount,
    editAccount,
    resetEditAccount,
    deleteAccount,
    resetDeleteAccount,
    getAccounts,
    getPagedAccounts,
    getAccount,
    resetAccount,
    resetStates,
  };
}

/**
 * REDUCERS
 */
function createExtraReducers(
  builder: ActionReducerMapBuilder<AccountStateTypes>
) {
  return {
    ...createAccountReducer(),
    ...resetCreateAccount(),
    ...editAccountReducer(),
    ...resetEditAccount(),
    ...deleteAccountReducer(),
    ...resetDeleteAccount(),
    ...accountsReducer(),
    ...pagedAccountsReducer(),
    ...accountReducer(),
    ...resetAccount(),
    ...resetStatesReducer(),
  };

  function createAccountReducer() {
    return {
      ...builder.addCase(
        extraActions.createAccount.pending,
        (state, action) => {
          state.createAccount = {
            loading: true,
            payload: null,
            successful: false,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.createAccount.fulfilled,
        (state, action) => {
          state.createAccount = {
            loading: false,
            payload: action.payload.message,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.createAccount.rejected,
        (state, action) => {
          state.createAccount = {
            loading: false,
            payload: null,
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function resetCreateAccount() {
    return {
      ...builder.addCase(extraActions.resetCreateAccount, (state, action) => {
        state.resetCreateAccount = ResetApiState(true);
        state.createAccount = ResetApiState(null);
      }),
    };
  }

  function editAccountReducer() {
    return {
      ...builder.addCase(extraActions.editAccount.pending, (state, action) => {
        state.editAccount = {
          loading: true,
          payload: null,
          successful: false,
          error: null,
        };
      }),
      ...builder.addCase(
        extraActions.editAccount.fulfilled,
        (state, action) => {
          state.editAccount = {
            loading: false,
            payload: action.payload.message,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(extraActions.editAccount.rejected, (state, action) => {
        state.editAccount = {
          loading: false,
          payload: null,
          successful: false,
          error: action.payload,
        };
      }),
    };
  }

  function resetEditAccount() {
    return {
      ...builder.addCase(extraActions.resetEditAccount, (state, action) => {
        state.resetEditAccount = ResetApiState(true);
        state.editAccount = ResetApiState(null);
      }),
    };
  }

  function deleteAccountReducer() {
    return {
      ...builder.addCase(
        extraActions.deleteAccount.pending,
        (state, action) => {
          state.deleteAccount = {
            loading: true,
            payload: null,
            successful: false,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.deleteAccount.fulfilled,
        (state, action) => {
          state.deleteAccount = {
            loading: false,
            payload: true,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.deleteAccount.rejected,
        (state, action) => {
          state.deleteAccount = {
            loading: false,
            payload: null,
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function resetDeleteAccount() {
    return {
      ...builder.addCase(extraActions.resetDeleteAccount, (state, action) => {
        state.resetDeleteAccount = ResetApiState(true);
        state.deleteAccount = ResetApiState(null);
      }),
    };
  }

  function accountsReducer() {
    return {
      ...builder.addCase(extraActions.getAccounts.pending, (state, action) => {
        state.accounts = {
          loading: true,
          payload: [],
          successful: false,
          error: null,
        };
      }),
      ...builder.addCase(
        extraActions.getAccounts.fulfilled,
        (state, action) => {
          state.accounts = {
            loading: false,
            payload: action.payload,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(extraActions.getAccounts.rejected, (state, action) => {
        state.accounts = {
          loading: false,
          payload: [],
          successful: false,
          error: action.payload,
        };
      }),
    };
  }

  function pagedAccountsReducer() {
    return {
      ...builder.addCase(
        extraActions.getPagedAccounts.pending,
        (state, action) => {
          state.pagedAccounts = {
            loading: true,
            payload: ResetPaginationState(),
            successful: false,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getPagedAccounts.fulfilled,
        (state, action) => {
          state.pagedAccounts = {
            loading: false,
            payload: action.payload,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getPagedAccounts.rejected,
        (state, action) => {
          state.pagedAccounts = {
            loading: false,
            payload: ResetPaginationState(),
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function accountReducer() {
    return {
      ...builder.addCase(extraActions.getAccount.pending, (state, action) => {
        state.account = {
          loading: true,
          payload: null,
          successful: false,
          error: null,
        };
      }),
      ...builder.addCase(extraActions.getAccount.fulfilled, (state, action) => {
        state.account = {
          loading: false,
          payload: action.payload,
          successful: true,
          error: null,
        };
      }),
      ...builder.addCase(extraActions.getAccount.rejected, (state, action) => {
        state.account = {
          loading: false,
          payload: null,
          successful: false,
          error: action.payload,
        };
      }),
    };
  }

  function resetAccount() {
    return {
      ...builder.addCase(extraActions.resetAccount, (state, action) => {
        state.resetAccount = ResetApiState(true);
        state.account = ResetApiState(null);
      }),
    };
  }

  function resetStatesReducer() {
    return {
      ...builder.addCase(extraActions.resetStates, (state, action) => {
        state.resetStates = ResetApiState(true);

        state.createAccount = ResetApiState(null);
        state.resetCreateAccount = ResetApiState(null);
        state.editAccount = ResetApiState(null);
        state.resetEditAccount = ResetApiState(null);
        state.deleteAccount = ResetApiState(null);
        state.resetDeleteAccount = ResetApiState(null);
        state.accounts = ResetApiState([]);
        state.account = ResetApiState(null);
        state.resetAccount = ResetApiState(null);
        state.resetAccount = ResetApiState(null);
      }),
    };
  }
}

const AccountSlice = createSlice({
  name,
  initialState: AccountInitialState,
  reducers: {},
  extraReducers: (builder) => createExtraReducers(builder),
});

export const AccountActions = {
  ...AccountSlice.actions,
  ...extraActions,
};
export const AccountReducer = AccountSlice.reducer;
